/**
 * Lightweight, JS-readable hint that the user *might* have an active session.
 *
 * The real session lives in httpOnly `accessToken`/`refreshToken` cookies that
 * the browser cannot read. Without a hint, every page would have to fire
 * `GET /api/v1/auth/me` just to discover that anonymous visitors are anonymous,
 * which produces a guaranteed 401 on every public page.
 *
 * Login/register set the hint; logout clears it. The hint is intentionally
 * advisory: a true value still requires `/auth/me` to validate the session, and
 * a false value cleanly skips the bootstrap query for anonymous traffic.
 */

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'loci.session.present';

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function hasSessionHint(): boolean {
  if (!isBrowser()) return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setSessionHint(): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // localStorage may be unavailable (private mode, quota) — fall through silently.
  }
}

export function clearSessionHint(): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Read the session hint in a way that is safe for both SSR and CSR hydration.
 *
 * Returns `false` on the first render (server and client agree → no hydration
 * mismatch), then flips to the real value after the mount `useEffect` runs.
 * Callers thread the return value into an RTK Query `skip` option so the
 * `/auth/me` probe never fires for anonymous visitors but resumes the moment a
 * session is established (login writes the hint synchronously).
 */
export function useSessionHint(): boolean {
  const [present, setPresent] = useState(false);
  useEffect(() => {
    setPresent(hasSessionHint());
  }, []);
  return present;
}
